import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatNumber, toNumber } from '@/lib/utils';

import { alertaOsinerming, ALERTA_CLASES, porcentaje } from '../lib/ric-format';
import type { RicBloque, RicConsolidado, RicFilaDia } from '../ric.types';

/** Columnas de la tabla diaria del RIC (visual y Osinerming). */
const COLUMNAS =
  'grid grid-cols-[2.5rem_6rem_6rem_6rem_6.5rem_6.5rem_6rem_6rem_6.5rem_6.5rem] items-center gap-x-2';

/** Columnas extra que sólo muestra Osinerming (las calculaba su JavaScript). */
const COLUMNAS_OSINERMING =
  'grid grid-cols-[2.5rem_6rem_6rem_6.5rem_6.5rem_6.5rem_5.5rem_6.5rem_5.5rem_6.5rem_6.5rem_6.5rem] items-center gap-x-2';

interface ConsolidadoRicProps {
  data: RicConsolidado | undefined;
  bloques: RicBloque[] | undefined;
  loading: boolean;
  /** Muestra las columnas de recepción/estación y las alertas de Osinerming. */
  osinerming?: boolean;
  tituloBloque: string;
}

/**
 * Consolidado mensual del RIC.
 *
 * Se usa para el *reporte visual* (una pestaña por tanque) y para el de
 * *Osinerming* (una pestaña por producto padre, con columnas y alertas propias).
 * Las filas llegan ya calculadas desde el backend; aquí sólo se pinta.
 */
export function ConsolidadoRic({
  data,
  bloques,
  loading,
  osinerming = false,
  tituloBloque,
}: ConsolidadoRicProps) {
  const [activo, setActivo] = useState(0);

  /** Tabla continua de días 1..N, sin huecos. */
  const filas = useMemo(() => bloques?.[activo]?.rows ?? [], [bloques, activo]);
  const bloque = bloques?.[activo];

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 rounded-control" />
        <Skeleton className="h-72 rounded-card" />
      </div>
    );
  }

  if (!data || !bloques || bloques.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
        <p className="text-sm font-medium text-ink-700">Sin datos para el período</p>
        <p className="mt-0.5 text-xs text-ink-500">
          El mes seleccionado todavía no tiene tomas de inventario registradas.
        </p>
      </div>
    );
  }

  const columnas = osinerming ? COLUMNAS_OSINERMING : COLUMNAS;

  return (
    <div className="space-y-4">
      {/* Pestañas por tanque o producto */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label={tituloBloque}>
        {bloques.map((item, indice) => (
          <button
            key={item.tank_label}
            type="button"
            role="tab"
            aria-selected={indice === activo}
            onClick={() => setActivo(indice)}
            className={cn(
              'rounded-control px-3 py-1.5 text-xs font-semibold transition-colors',
              indice === activo
                ? 'bg-brand-600 text-white'
                : 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700',
            )}
          >
            {osinerming ? `P${item.group_number} · ${item.product_label}` : item.tank_label}
          </button>
        ))}
      </div>

      {bloque ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
          <span className="font-medium text-ink-700">{bloque.tank_label}</span>
          <Badge variant="outline">Ingresos {formatNumber(bloque.totals.incomes)} gal</Badge>
          <Badge variant="outline">Salidas {formatNumber(bloque.totals.outcomes)} gal</Badge>
          <Badge variant={bloque.totals.difference < 0 ? 'danger' : 'warning'}>
            Diferencia {formatNumber(bloque.totals.difference)} gal
          </Badge>
          {osinerming && bloque.tank_ids ? (
            <span>Tanques: {bloque.tank_ids.join(', ')}</span>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        {/* El ancho mínimo cubre la suma de columnas + separaciones, para que
            ninguna celda quede recortada al desplazar en pantallas angostas. */}
        <div className={cn(osinerming ? 'min-w-[79rem]' : 'min-w-[64rem]')}>
          <div
            className={cn(
              columnas,
              'border-b border-ink-100 pb-2 text-[10px] font-semibold tracking-wider text-ink-400 uppercase',
            )}
          >
            <span>Día</span>
            <span className="text-right">Previo</span>
            <span className="text-right">Ingresos</span>
            <span className="text-right">Salidas</span>
            <span className="text-right">Teórica</span>
            <span className="text-right">Físico</span>
            <span className="text-right">Agua</span>
            <span className="text-right">Físico s/agua</span>
            <span className="text-right">Retornos</span>
            <span className="text-right">Diferencia</span>
            {osinerming ? (
              <>
                <span className="text-right">Recepción</span>
                <span className="text-right">Estación</span>
                <span className="text-center">Alerta</span>
              </>
            ) : null}
          </div>

          <ul className="mt-1">
            {filas.map((fila) => (
              <FilaConsolidado
                key={fila.day}
                fila={fila}
                umbral={data.threshold}
                osinerming={osinerming}
              />
            ))}
          </ul>
        </div>
      </div>

      <p className="text-[11px] text-ink-400">
        Días 1 a {data.daysInMonth} de {data.start} al {data.end} · diferencia en rojo cuando supera
        el umbral de {formatNumber(data.threshold)} gal.
      </p>
    </div>
  );
}

/** Fila diaria del consolidado. */
function FilaConsolidado({
  fila,
  umbral,
  osinerming,
}: {
  fila: RicFilaDia;
  umbral: number;
  osinerming: boolean;
}) {
  const logica = toNumber(fila.previous) + toNumber(fila.incomes) - toNumber(fila.outcomes);
  const sinAgua = toNumber(fila.physical) - toNumber(fila.water_gl);
  const diferencia = sinAgua - logica;
  const sinMovimiento =
    !fila.previous && !fila.incomes && !fila.outcomes && !fila.physical && !fila.difference;

  // Columnas propias de Osinerming: recepción = recibido − ingresos; estación = retornos.
  const recepcion = toNumber(fila.received) - toNumber(fila.incomes);
  const estacion = toNumber(fila.returns);
  const totalDia = recepcion + estacion;
  const desvio = porcentaje(diferencia, toNumber(fila.outcomes) || toNumber(fila.incomes) || 1);
  const alerta = alertaOsinerming(toNumber(fila.outcomes), desvio);
  const fueraDeUmbral = Math.abs(diferencia) > (umbral > 0 ? umbral : 100);

  const columnas = osinerming ? COLUMNAS_OSINERMING : COLUMNAS;

  return (
    <li
      className={cn(
        columnas,
        'border-b border-ink-100 py-1.5 text-xs last:border-b-0',
        sinMovimiento && 'text-ink-300',
        fueraDeUmbral && 'bg-rose-50/70',
      )}
    >
      <span className="font-mono font-semibold text-ink-600 tabular-nums">
        {String(fila.day).padStart(2, '0')}
      </span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.previous)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.incomes)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.outcomes)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(logica)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.physical)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.water_gl)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(sinAgua)}</span>
      <span className="text-right font-mono tabular-nums">{formatNumber(fila.returns)}</span>
      <span
        className={cn(
          'text-right font-mono tabular-nums',
          fueraDeUmbral ? 'font-semibold text-rose-600' : 'text-ink-800',
        )}
      >
        {formatNumber(diferencia)}
      </span>

      {osinerming ? (
        <>
          <span className="text-right font-mono tabular-nums">
            {formatNumber(recepcion)}
            <span className="block text-[10px] text-ink-400">
              {formatNumber(totalDia)} total ({formatNumber(porcentaje(recepcion, totalDia || 1))} %)
            </span>
          </span>
          <span className="text-right font-mono tabular-nums">
            {formatNumber(estacion)}
            <span className="block text-[10px] text-ink-400">
              {formatNumber(porcentaje(estacion, toNumber(fila.incomes) || 1))} % de ingresos
            </span>
          </span>
          <span className="flex justify-center">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                ALERTA_CLASES[alerta],
              )}
            >
              {alerta}
            </span>
          </span>
        </>
      ) : null}
    </li>
  );
}
